import { useMemo, useState } from 'react';
import { updateVehicle, type Contribution, type Vehicle } from '../api';

interface Props {
  accountId: string;
  vehicles: Vehicle[];
  onChange: () => void;
}

const ANNUAL_ALLOWANCE_PENCE = 60_000 * 100; // £60k AA per person
const ISA_ALLOWANCE_PENCE = 20_000 * 100; // £20k ISA per person per year

export function ContributionsSection({ accountId, vehicles, onChange }: Props) {
  const taxYears = useMemo(() => collectTaxYears(vehicles), [vehicles]);
  const [selectedTY, setSelectedTY] = useState(taxYears[0] ?? currentTaxYear());

  const summary = useMemo(() => summarise(vehicles, selectedTY), [vehicles, selectedTY]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ color: '#475569', fontSize: '0.9rem' }}>UK tax year</span>
        <select value={selectedTY} onChange={(e) => setSelectedTY(e.target.value)}>
          {[currentTaxYear(), ...taxYears.filter((t) => t !== currentTaxYear())].map((t) => (
            <option key={t} value={t}>
              {formatTaxYear(t)}
            </option>
          ))}
        </select>
        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
          UK tax year runs 6 Apr → 5 Apr.
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '0.75rem',
          marginTop: '1rem',
        }}
      >
        {summary.byOwner.map((o) => (
          <div
            key={o.owner}
            style={{
              border: '1px solid #e2e8f0',
              borderRadius: '0.5rem',
              padding: '0.75rem',
            }}
          >
            <div style={{ fontWeight: 600 }}>{o.owner}</div>
            <CapBar
              label="Pension AA"
              used={o.pensionPence}
              cap={ANNUAL_ALLOWANCE_PENCE}
              note="Combined SIPP/pension contributions (employer + personal)"
            />
            <CapBar
              label="ISA"
              used={o.isaPence}
              cap={ISA_ALLOWANCE_PENCE}
              note="ISA subscriptions"
            />
            {o.otherPence > 0 && (
              <div style={{ marginTop: '0.4rem', fontSize: '0.85rem', color: '#475569' }}>
                Other wrappers: {formatGBP(o.otherPence)}
              </div>
            )}
            <div
              style={{
                fontSize: '0.85rem',
                color: '#475569',
                marginTop: '0.4rem',
                paddingTop: '0.4rem',
                borderTop: '1px dashed #e2e8f0',
              }}
            >
              <div>Employer: {formatGBP(o.employerPence)}</div>
              <div>Personal: {formatGBP(o.personalPence)}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: '1.25rem' }}>
        <h4 style={{ marginBottom: '0.5rem' }}>Per-vehicle</h4>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
              <Th>Vehicle</Th>
              <Th>Owner</Th>
              <Th align="right">Employer</Th>
              <Th align="right">Personal</Th>
              <Th align="right">Total</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v) => (
              <VehicleContribRow
                key={v.id}
                accountId={accountId}
                vehicle={v}
                taxYear={selectedTY}
                onSaved={onChange}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VehicleContribRow({
  accountId,
  vehicle,
  taxYear,
  onSaved,
}: {
  accountId: string;
  vehicle: Vehicle;
  taxYear: string;
  onSaved: () => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const inTY = (vehicle.contributions ?? []).filter((c) => taxYearOf(c.date) === taxYear);
  const employer = inTY
    .filter((c) => c.source === 'employer')
    .reduce((s, c) => s + c.amount_pence, 0);
  const personal = inTY
    .filter((c) => c.source !== 'employer')
    .reduce((s, c) => s + c.amount_pence, 0);
  const total = employer + personal;

  return (
    <>
      <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
        <Td>{vehicle.name}</Td>
        <Td>{vehicle.owner}</Td>
        <Td align="right">{employer > 0 ? formatGBP(employer) : '—'}</Td>
        <Td align="right">{personal > 0 ? formatGBP(personal) : '—'}</Td>
        <Td align="right">{total > 0 ? formatGBP(total) : '—'}</Td>
        <Td align="right">
          <button onClick={() => setShowAdd((s) => !s)} style={{ fontSize: '0.85rem' }}>
            {showAdd ? 'Close' : 'Add / view'}
          </button>
        </Td>
      </tr>
      {showAdd && (
        <tr>
          <td colSpan={6} style={{ padding: '0.5rem 0.4rem', background: '#f8fafc' }}>
            <ContribEditor
              accountId={accountId}
              vehicle={vehicle}
              taxYear={taxYear}
              onSaved={onSaved}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function ContribEditor({
  accountId,
  vehicle,
  taxYear,
  onSaved,
}: {
  accountId: string;
  vehicle: Vehicle;
  taxYear: string;
  onSaved: () => void;
}) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [source, setSource] = useState<'employer' | 'personal'>('personal');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const tyContribs = (vehicle.contributions ?? []).filter((c) => taxYearOf(c.date) === taxYear);

  async function add() {
    const amt = Number(amount);
    if (!amt || amt <= 0) return;
    setSaving(true);
    try {
      const newContrib: Contribution = {
        id: crypto.randomUUID(),
        date,
        amount_pence: Math.round(amt * 100),
        source,
        note: note.trim() || undefined,
      };
      const next = [...(vehicle.contributions ?? []), newContrib];
      await updateVehicle(accountId, vehicle.id, {
        name: vehicle.name,
        owner: vehicle.owner,
        kind: vehicle.kind,
        cash_pence: vehicle.cash_pence,
        platform_fee_pct_per_year: vehicle.platform_fee_pct_per_year,
        platform_fee_flat_pence: vehicle.platform_fee_flat_pence,
        contributions: next,
      });
      setAmount('');
      setNote('');
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    setSaving(true);
    try {
      const next = (vehicle.contributions ?? []).filter((c) => c.id !== id);
      await updateVehicle(accountId, vehicle.id, {
        name: vehicle.name,
        owner: vehicle.owner,
        kind: vehicle.kind,
        cash_pence: vehicle.cash_pence,
        platform_fee_pct_per_year: vehicle.platform_fee_pct_per_year,
        platform_fee_flat_pence: vehicle.platform_fee_flat_pence,
        contributions: next,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <input
          type="number"
          step="0.01"
          min="0"
          placeholder="Amount £"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          style={{ width: '8rem' }}
        />
        <select
          value={source}
          onChange={(e) => setSource(e.target.value as 'employer' | 'personal')}
        >
          <option value="personal">Personal</option>
          <option value="employer">Employer</option>
        </select>
        <input
          placeholder="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ flex: 1, minWidth: '10rem' }}
        />
        <button onClick={add} disabled={saving || !amount}>
          {saving ? '…' : 'Add'}
        </button>
      </div>

      {tyContribs.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
          <thead>
            <tr style={{ color: '#64748b', fontSize: '0.85rem', textAlign: 'left' }}>
              <Th>Date</Th>
              <Th align="right">Amount</Th>
              <Th>Source</Th>
              <Th>Note</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {tyContribs
              .slice()
              .sort((a, b) => b.date.localeCompare(a.date))
              .map((c) => (
                <tr key={c.id}>
                  <Td>{c.date}</Td>
                  <Td align="right">{formatGBP(c.amount_pence)}</Td>
                  <Td>{c.source}</Td>
                  <Td>{c.note ?? ''}</Td>
                  <Td align="right">
                    <button onClick={() => remove(c.id)} disabled={saving}>
                      ×
                    </button>
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CapBar({
  label,
  used,
  cap,
  note,
}: {
  label: string;
  used: number;
  cap: number;
  note?: string;
}) {
  const pct = Math.min(100, (used / cap) * 100);
  const remaining = Math.max(0, cap - used);
  const tone = pct >= 95 ? '#dc2626' : pct >= 75 ? '#d97706' : '#059669';
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <span style={{ color: '#475569' }}>
          {formatGBP(used)} / {formatGBP(cap)}
        </span>
      </div>
      <div
        style={{ background: '#e2e8f0', height: '8px', borderRadius: '4px', overflow: 'hidden' }}
      >
        <div style={{ background: tone, height: '100%', width: `${pct}%` }} />
      </div>
      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.2rem' }}>
        {formatGBP(remaining)} remaining
        {note ? ` · ${note}` : ''}
      </div>
    </div>
  );
}

interface OwnerSummary {
  owner: string;
  pensionPence: number;
  isaPence: number;
  otherPence: number;
  employerPence: number;
  personalPence: number;
}

function summarise(vehicles: Vehicle[], taxYear: string): { byOwner: OwnerSummary[] } {
  const byOwner = new Map<string, OwnerSummary>();
  for (const v of vehicles) {
    const tyContribs = (v.contributions ?? []).filter((c) => taxYearOf(c.date) === taxYear);
    if (tyContribs.length === 0) continue;
    const o = byOwner.get(v.owner) ?? {
      owner: v.owner,
      pensionPence: 0,
      isaPence: 0,
      otherPence: 0,
      employerPence: 0,
      personalPence: 0,
    };
    const total = tyContribs.reduce((s, c) => s + c.amount_pence, 0);
    const employer = tyContribs
      .filter((c) => c.source === 'employer')
      .reduce((s, c) => s + c.amount_pence, 0);
    o.employerPence += employer;
    o.personalPence += total - employer;
    const kind = (v.kind ?? '').toLowerCase();
    if (kind.includes('isa')) o.isaPence += total;
    else if (kind.includes('sipp') || kind.includes('pension')) o.pensionPence += total;
    else o.otherPence += total;
    byOwner.set(v.owner, o);
  }
  // Always render every owner with vehicles, even if no contributions logged yet.
  for (const v of vehicles) {
    if (!byOwner.has(v.owner)) {
      byOwner.set(v.owner, {
        owner: v.owner,
        pensionPence: 0,
        isaPence: 0,
        otherPence: 0,
        employerPence: 0,
        personalPence: 0,
      });
    }
  }
  return { byOwner: Array.from(byOwner.values()).sort((a, b) => a.owner.localeCompare(b.owner)) };
}

function collectTaxYears(vehicles: Vehicle[]): string[] {
  const set = new Set<string>();
  for (const v of vehicles) for (const c of v.contributions ?? []) set.add(taxYearOf(c.date));
  set.add(currentTaxYear());
  return Array.from(set).sort().reverse();
}

// UK tax year runs 6 April YYYY → 5 April YYYY+1. Encode as the start year.
function taxYearOf(date: string): string {
  const d = new Date(date);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth(); // 0-indexed
  const day = d.getUTCDate();
  // Before 6 April → previous tax year.
  if (m < 3 || (m === 3 && day < 6)) return String(y - 1);
  return String(y);
}

function currentTaxYear(): string {
  return taxYearOf(new Date().toISOString().slice(0, 10));
}

function formatTaxYear(start: string): string {
  const s = Number(start);
  return `${s}/${String(s + 1).slice(-2)} (6 Apr ${s} – 5 Apr ${s + 1})`;
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'right' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: '0.4rem',
        fontWeight: 500,
        fontSize: '0.85rem',
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children?: React.ReactNode; align?: 'right' }) {
  return <td style={{ textAlign: align ?? 'left', padding: '0.4rem' }}>{children}</td>;
}

const gbp = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
});
function formatGBP(pence: number): string {
  return gbp.format(pence / 100);
}
